/**
 * API キーと埋め込み。キーの発行・失効と、呼び出しを許可するオリジンの設定。
 *
 * この面が守っていること:
 * - **秘密の値は発行直後の 1 回だけ出す。** 閉じたら消え、取り直す口は無い。
 *   一覧に出るのはマスク済みの表示（`vdp_xxxxxxxx…abcd`）だけ
 * - **可否はサーバーの答えで決める。** 押せるかどうかは
 *   `allowedOrigins.editableByViewer`（＝ サーバーの `decide()` の答え）で、
 *   画面に役割の表を写さない
 * - 配信 API の使い方と、記録のある月だけの利用量を表示する
 * - 時刻は生の ISO を出さず、日本時間の表記に揃える
 * - 版（楽観ロック）は必ずサーバーから読んだ値を返す。読まずに書く経路を作らない
 */

import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  ApiAccessDocument,
  Button,
  Card,
  CreateApiKeyDocument,
  ErrorState,
  formatJstDateTime,
  LoadingState,
  PageHeader,
  RevokeApiKeyDocument,
  SetAllowedOriginsDocument,
  Tag,
} from "visumo-data-agent-web-shared";

import {
  ALLOWED_ORIGIN_FORMAT_NOTE,
  ALLOWED_ORIGINS_EMPTY_NOTE,
  API_ACCESS_FORBIDDEN_NOTE,
  API_KEY_PLAINTEXT_SHOWN_ONCE_NOTE,
  API_KEY_REVOKE_NOTE,
  API_KEYS_EMPTY_NOTE,
  MAX_ALLOWED_ORIGINS,
  MAX_API_KEY_LABEL_LENGTH,
} from "../api-capabilities";
import { HelpIcon } from "../help/HelpIcon";

interface PageUserError {
  readonly code: string;
  readonly message: string;
}

/** 一覧に出す状態の表示名。API の値をそのまま出さない。 */
function statusOf(revokedAt: string | null): {
  label: string;
  tone: "ok" | "neutral";
} {
  return revokedAt === null
    ? { label: "使えます", tone: "ok" }
    : { label: "失効済み", tone: "neutral" };
}

/** 入力欄の 1 行ずつをオリジンの一覧にする。空行は捨てる。 */
export function parseOriginsInput(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function ApiKeysPage() {
  const [result, refetch] = useQuery({
    query: ApiAccessDocument,
    // ほかの端末からも増える。開き直したら読み直す
    requestPolicy: "cache-and-network",
  });
  const [createState, create] = useMutation(CreateApiKeyDocument);
  const [revokeState, revoke] = useMutation(RevokeApiKeyDocument);
  const [originsState, saveOrigins] = useMutation(SetAllowedOriginsDocument);
  const [userErrors, setUserErrors] = useState<PageUserError[]>([]);
  const [label, setLabel] = useState("");
  const [originsDraft, setOriginsDraft] = useState<string | null>(null);
  /** 発行直後に 1 回だけ出す値。閉じると消える（再表示の口は無い）。 */
  const [issued, setIssued] = useState<{
    label: string;
    plaintext: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  if (result.fetching && result.data === undefined) {
    return <LoadingState />;
  }
  if (result.error !== undefined || result.data === undefined) {
    return (
      <ErrorState
        description={result.error?.message}
        onRetry={() => refetch({ requestPolicy: "network-only" })}
      />
    );
  }

  const apiKeys = result.data.apiKeys;
  const allowedOrigins = result.data.allowedOrigins;
  // **判断の正本はサーバー。** キーの区画とオリジンの区画は同じ判断
  // （`ADMINISTER` / `API_KEY`）なので、1 つの値で両方を決める
  const editable = allowedOrigins.editableByViewer;
  const busy =
    createState.fetching || revokeState.fetching || originsState.fetching;
  const originsText = originsDraft ?? allowedOrigins.origins.join("\n");

  const failWith = (message: string): void => {
    setUserErrors([{ code: "INTERNAL_ERROR", message }]);
  };

  const submitCreate = (): void => {
    const trimmed = label.trim();
    if (trimmed.length === 0) return;
    void create({
      input: { label: trimmed, scope: "DELIVERY_READ" },
    }).then((response) => {
      if (response.error !== undefined) {
        failWith(`キーを発行できませんでした: ${String(response.error)}`);
        return;
      }
      const payload = response.data?.createApiKey;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      if (payload.plaintext !== null && payload.plaintext !== undefined) {
        setIssued({ label: trimmed, plaintext: payload.plaintext });
        setCopied(false);
      }
      setLabel("");
      refetch({ requestPolicy: "network-only" });
    });
  };

  const submitRevoke = (keyId: string, expectedVersion: number): void => {
    void revoke({ input: { keyId, expectedVersion } }).then((response) => {
      if (response.error !== undefined) {
        failWith(`失効させられませんでした: ${String(response.error)}`);
        return;
      }
      const payload = response.data?.revokeApiKey;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      refetch({ requestPolicy: "network-only" });
    });
  };

  const submitOrigins = (): void => {
    void saveOrigins({
      input: {
        origins: parseOriginsInput(originsText),
        // **読んだ版をそのまま返す。** 画面で版を作らない
        expectedVersion: allowedOrigins.version,
      },
    }).then((response) => {
      if (response.error !== undefined) {
        failWith(`保存できませんでした: ${String(response.error)}`);
        return;
      }
      const payload = response.data?.setAllowedOrigins;
      if (payload === undefined) return;
      if (payload.userErrors.length > 0) {
        setUserErrors(
          payload.userErrors.map(({ code, message }) => ({ code, message })),
        );
        return;
      }
      setUserErrors([]);
      setOriginsDraft(null);
      refetch({ requestPolicy: "network-only" });
    });
  };

  const copyPlaintext = (): void => {
    if (issued === null) return;
    void navigator.clipboard
      ?.writeText(issued.plaintext)
      .then(() => setCopied(true))
      .catch(() => setCopied(false));
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="API キーと埋め込み"
        description="自社のサイトから、承認して配信している答えを読みにくるためのキーを発行します。読み取りだけで、書き換える口はありません。"
        titleExtra={<HelpIcon slug="integrations/api-keys" />}
      />

      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-notice vd-notice--error" role="alert">
            {userErrors.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        {issued === null ? null : (
          <Card
            title="発行した API キー"
            note={API_KEY_PLAINTEXT_SHOWN_ONCE_NOTE}
          >
            <div className="vd-stack">
              <p style={{ margin: 0 }}>{issued.label} のキーです。</p>
              <code
                className="vd-input"
                data-testid="api-key-plaintext"
                style={{ display: "block", wordBreak: "break-all" }}
              >
                {issued.plaintext}
              </code>
              <div className="vd-row">
                <Button variant="primary" onClick={copyPlaintext}>
                  コピーする
                </Button>
                <Button
                  onClick={() => {
                    setIssued(null);
                    setCopied(false);
                  }}
                >
                  閉じる（もう表示できません）
                </Button>
                {copied ? <Tag tone="ok">コピーしました</Tag> : null}
              </div>
            </div>
          </Card>
        )}

        <Card
          title="API キー"
          note={API_KEY_REVOKE_NOTE}
          extra={<HelpIcon slug="integrations/api-keys" anchor="API-キー" />}
        >
          {editable ? null : (
            <p
              className="vd-notice"
              role="status"
              data-testid="api-access-forbidden"
            >
              {API_ACCESS_FORBIDDEN_NOTE}
            </p>
          )}
          {apiKeys.length === 0 ? (
            <p className="vd-card-note" style={{ marginTop: 0 }}>
              {API_KEYS_EMPTY_NOTE}
            </p>
          ) : (
            <div className="vd-table-scroll">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>キー</th>
                    <th>用途</th>
                    <th>作った人</th>
                    <th>作成日</th>
                    <th>最後に使われた日時</th>
                    <th>状態</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((apiKey) => {
                    const status = statusOf(apiKey.revokedAt ?? null);
                    return (
                      <tr key={apiKey.id} data-testid="api-key-row">
                        <td>
                          {/* 秘密の断片はここだけ。復元はできない */}
                          <code>{apiKey.masked}</code>
                        </td>
                        <td>{apiKey.label}</td>
                        <td>{apiKey.createdBy?.displayName ?? "—"}</td>
                        <td>{formatJstDateTime(apiKey.createdAt)}</td>
                        <td>
                          {apiKey.lastUsedAt
                            ? formatJstDateTime(apiKey.lastUsedAt)
                            : "直近2か月の記録なし"}
                        </td>
                        <td>
                          <Tag tone={status.tone}>{status.label}</Tag>
                        </td>
                        <td>
                          {apiKey.revokedAt === null ? (
                            <Button
                              disabled={busy || !editable}
                              aria-label={`${apiKey.label} のキーを失効させる`}
                              onClick={() =>
                                submitRevoke(apiKey.id, apiKey.version)
                              }
                            >
                              失効させる
                            </Button>
                          ) : (
                            <span className="vd-stat-note">
                              {formatJstDateTime(apiKey.revokedAt)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="キーを発行する"
          extra={
            <HelpIcon slug="integrations/api-keys" anchor="キーを発行する" />
          }
          note="発行した直後の 1 回しか表示できません。"
        >
          <div className="vd-stack">
            <label className="vd-field">
              <span className="vd-field-hint">用途</span>
              <input
                className="vd-input"
                value={label}
                maxLength={MAX_API_KEY_LABEL_LENGTH}
                placeholder="例: 自社サイトの商品ページ"
                disabled={!editable}
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            <div>
              <Button
                variant="primary"
                disabled={busy || !editable || label.trim().length === 0}
                onClick={submitCreate}
              >
                キーを発行する
              </Button>
            </div>
          </div>
        </Card>

        <Card
          title="許可するオリジン"
          note={ALLOWED_ORIGIN_FORMAT_NOTE}
          extra={
            <HelpIcon slug="integrations/api-keys" anchor="許可するオリジン" />
          }
        >
          <div className="vd-stack">
            {allowedOrigins.origins.length === 0 ? (
              <p className="vd-card-note" style={{ marginTop: 0 }}>
                {ALLOWED_ORIGINS_EMPTY_NOTE}
              </p>
            ) : null}
            <label className="vd-field">
              <span className="vd-field-hint">
                {`呼び出しを許可するサイト（${String(MAX_ALLOWED_ORIGINS)} 件まで）`}
              </span>
              <textarea
                className="vd-input"
                rows={5}
                value={originsText}
                disabled={!editable}
                data-testid="allowed-origins-input"
                placeholder="https://shop.example.com"
                onChange={(event) => setOriginsDraft(event.target.value)}
              />
            </label>
            {allowedOrigins.updatedAt === null ? null : (
              <p className="vd-stat-note" style={{ margin: 0 }}>
                {`最後に変えたのは ${formatJstDateTime(allowedOrigins.updatedAt)} です。`}
              </p>
            )}
            <div>
              <Button
                variant="primary"
                disabled={busy || !editable}
                onClick={submitOrigins}
              >
                保存する
              </Button>
            </div>
          </div>
        </Card>
        <Card
          title="はじめかた（埋め込み）"
          extra={<HelpIcon slug="integrations/api-keys" />}
        >
          <p>
            ブラウザに埋めるキーは公開される前提です。読み取り専用のキーを使い、呼び出すサイトのオリジンを登録してください。キーごとのレート制限も適用されます。
          </p>
          <p>
            サーバー間の呼び出しはキーで認証します。Origin
            の許可は、キーを秘密に保つ仕組みではありません。
          </p>
          <p>
            YOUR_PRODUCT_ID を商品 ID に、YOUR_DELIVERY_API_KEY
            を発行したキーに置き換えてください。ページの JavaScript 内（async
            関数またはモジュール）で実行します。
          </p>
          <p>JSON-LD はページ上で読める回答と同じ内容を使ってください。</p>
          {result.data.embedSnippets.map((snippet) => (
            <section key={snippet.kind}>
              <h3>{snippet.label}</h3>
              <pre style={{ overflowX: "auto" }}>
                <code>{snippet.code}</code>
              </pre>
            </section>
          ))}
        </Card>
        <Card
          title="リファレンス"
          extra={<HelpIcon slug="integrations/api-keys" />}
        >
          {result.data.apiEndpointDocs.map((doc) => (
            <section key={doc.path}>
              <p>
                <code>
                  {doc.method} {doc.path}
                </code>
              </p>
              <p>{doc.returns}</p>
              <p>{doc.rateLimit}</p>
            </section>
          ))}
          <p>
            Authorization: Bearer にキーを指定します。認証できないときは
            401、許可のないオリジンは 403、上限を超えると 429 を返します。
          </p>
          <p>
            回答はキャッシュせず、毎回キーと配信状態を確認します。商品一覧や書き込みの
            API はありません。
          </p>
        </Card>
        <Card
          title="利用量"
          extra={<HelpIcon slug="integrations/api-keys" anchor="利用量" />}
          note="過去6か月のうち記録のある月だけを表示します。月の区切りは UTC です。認証前の失敗は集計に含みません。計測障害時は実際の回数より少なくなる場合があります。"
        >
          {result.data.apiUsage.length === 0 ? (
            <p>まだ呼ばれていません（配信 API の導入月から数えています）。</p>
          ) : (
            <>
              <p>
                この期間の最初の記録は {result.data.apiUsage[0]?.period} です。
              </p>
              <div className="vd-table-scroll">
                <table className="vd-table">
                  <thead>
                    <tr>
                      <th>月</th>
                      <th>成功した呼び出し</th>
                      <th>失敗した呼び出し</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.apiUsage.map((row) => (
                      <tr key={row.period}>
                        <td>{row.period}</td>
                        <td>{row.calls}</td>
                        <td>{row.errors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
